import type { FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'

import { AgentMode, CommandType, EventType } from '@/index'
import type {
  ClaudeSession,
  Command,
  ConversationTurn,
} from '@/features/events/conversation/state/types'
import { createClaudeSessionRuntime } from '@/features/events/conversation/state/types'
import { UserInputBubble } from '@/features/events/conversation/bubbles/UserInputBubble'
import { AgentNarrativeBubble } from '@/features/events/conversation/bubbles/AgentNarrativeBubble'
import { NotificationBubble } from '@/features/events/conversation/bubbles/NotificationBubble'
import { FileModificationBubble } from '@/features/events/conversation/bubbles/FileModificationBubble'
import { BashContextRenderer } from '@/features/events/conversation/renderers/BashContextRenderer'
import { PlanApprovalContextRenderer } from '@/features/events/conversation/renderers/PlanApprovalContextRenderer'
import type { BashToolInput, ExitPlanModeToolInput } from '@/features/events/shared/types'

import { HOME_CONTENT } from '../defaults'
import {
  CONVERSATION_DEMO_FRAMES,
  DEMO_RESET_PAUSE_MS,
  FINAL_FRAME_INDEX,
} from '../demo/data/mockConversationScript'
import type { DemoConversationFrame } from '../demo/data/mockConversationScript'
import { DemoConversationChrome, DemoQuestionsBubble } from '../demo/components'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SECTION_HEADING_ID = 'conversation-demo-heading'
const NOOP = () => {}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const ConversationDemoSection: FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const prefersReducedMotion = useRef(
    'matchMedia' in globalThis && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  const isReducedMotion = prefersReducedMotion.current

  if (isMobile) {
    return <MobileConversationCarousel reducedMotion={isReducedMotion} />
  }

  if (isReducedMotion) {
    return <ReducedMotionStatic />
  }

  return <AnimatedDemo />
}

// ---------------------------------------------------------------------------
// Animated demo (full playback)
// ---------------------------------------------------------------------------

const AnimatedDemo: FC = () => {
  const { frameIndex, frame, isPaused, togglePause, goToNext, goToPrev } =
    useDemoConversationPlayback()

  return (
    <Box
      component="section"
      role="region"
      aria-roledescription="carousel"
      aria-labelledby={SECTION_HEADING_ID}
      sx={sectionContainerSx}
    >
      <Typography component="h2" id={SECTION_HEADING_ID} sx={chapterTitleSx}>
        {HOME_CONTENT.conversation.title}
      </Typography>
      <Typography sx={chapterSubtitleSx}>{HOME_CONTENT.conversation.subtitle}</Typography>

      <Typography sx={visuallyHiddenSx}>
        {HOME_CONTENT.conversation.accessibilitySummary}
      </Typography>

      <Box sx={demoContainerSx} aria-live={isPaused ? 'polite' : 'off'} aria-atomic="false">
        <Box
          role="group"
          aria-roledescription="slide"
          aria-label={`Slide ${frameIndex + 1} of ${CONVERSATION_DEMO_FRAMES.length}`}
          aria-current="true"
          sx={slideWrapperSx}
        >
          <DemoConversationChrome
            agentMode={frame.agentMode}
            agentModeTransition={frame.agentModeTransition}
            statusIcon={frame.statusIcon}
            showPause={frame.showPause}
            showInject={frame.showInject}
            activity={frame.activity}
            frameKey={frameIndex}
          >
            <FrameBubble frame={frame} />
          </DemoConversationChrome>
        </Box>
      </Box>

      <Box sx={dotIndicatorRowSx} aria-hidden="true">
        {CONVERSATION_DEMO_FRAMES.map((f, index) => (
          <Box key={f.event.eventId} sx={index === frameIndex ? activeDotSx : inactiveDotSx} />
        ))}
      </Box>

      <CarouselControls
        isPaused={isPaused}
        togglePause={togglePause}
        goToNext={goToNext}
        goToPrev={goToPrev}
      />
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Reduced motion: static Frame 12
// ---------------------------------------------------------------------------

const ReducedMotionStatic: FC = () => {
  const finalFrame = CONVERSATION_DEMO_FRAMES[FINAL_FRAME_INDEX]
  if (!finalFrame) return null

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={SECTION_HEADING_ID}
      sx={sectionContainerSx}
    >
      <Typography component="h2" id={SECTION_HEADING_ID} sx={chapterTitleSx}>
        {HOME_CONTENT.conversation.title}
      </Typography>
      <Typography sx={chapterSubtitleSx}>{HOME_CONTENT.conversation.subtitle}</Typography>

      <Box sx={demoContainerSx}>
        <DemoConversationChrome
          agentMode={finalFrame.agentMode}
          agentModeTransition={finalFrame.agentModeTransition}
          statusIcon={finalFrame.statusIcon}
          showPause={finalFrame.showPause}
          showInject={finalFrame.showInject}
          activity={finalFrame.activity}
          frameKey={FINAL_FRAME_INDEX}
        >
          <FrameBubble frame={finalFrame} />
        </DemoConversationChrome>
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Mobile carousel — auto-rotating screenshots with cross-fade
// ---------------------------------------------------------------------------

interface CarouselFrame {
  readonly src: string
  readonly alt: string
  readonly durationMs: number
}

const CAROUSEL_FRAMES: readonly CarouselFrame[] = [
  {
    src: '/images/conversation/user_prompt.png',
    alt: 'User requests rate limiting with Redis sliding window',
    durationMs: 4000,
  },
  {
    src: '/images/conversation/agent_narrative_1.png',
    alt: 'Agent outlines approach with Redis INCR and sliding window',
    durationMs: 4500,
  },
  {
    src: '/images/conversation/bash_request.png',
    alt: 'Bash permission request to verify Redis connectivity',
    durationMs: 4000,
  },
  {
    src: '/images/conversation/bash_approved.png',
    alt: 'Bash permission approved, agent running tests',
    durationMs: 1500,
  },
  {
    src: '/images/conversation/agent_narrative_2.png',
    alt: 'Agent reports Redis connected, all tests pass',
    durationMs: 4500,
  },
  {
    src: '/images/conversation/agent_questions.png',
    alt: 'Agent asks about rate limit strategy for authenticated users',
    durationMs: 4000,
  },
  {
    src: '/images/conversation/user_answers.png',
    alt: 'User selects higher limits for authenticated users',
    durationMs: 1500,
  },
  {
    src: '/images/conversation/plan_request.png',
    alt: 'Plan approval request with implementation steps',
    durationMs: 4000,
  },
  {
    src: '/images/conversation/plan_approved.png',
    alt: 'Plan approved with auto-accept edits mode',
    durationMs: 2000,
  },
  {
    src: '/images/conversation/file_created.png',
    alt: 'File created: rateLimiter.ts with Redis middleware',
    durationMs: 4500,
  },
  {
    src: '/images/conversation/file_modified.png',
    alt: 'File modified: app.ts wiring rate limiter middleware',
    durationMs: 4500,
  },
  {
    src: '/images/conversation/agent_reply.png',
    alt: 'Agent summary: rate limiting is live with all tests passing',
    durationMs: 5000,
  },
]

interface UseFrameTimerReturn {
  readonly frameIndex: number
  readonly goToNext: () => void
  readonly goToPrev: () => void
}

function useFrameTimer(
  frameCount: number,
  getDurationMs: (index: number) => number,
  paused: boolean,
): UseFrameTimerReturn {
  const [frameIndex, setFrameIndex] = useState(0)
  const [isResetting, setIsResetting] = useState(false)
  const timerIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (paused) return

    const duration = isResetting ? DEMO_RESET_PAUSE_MS : getDurationMs(frameIndex)

    timerIdRef.current = setTimeout(() => {
      if (isResetting) {
        setIsResetting(false)
        setFrameIndex(0)
        return
      }

      const nextIndex = frameIndex + 1
      if (nextIndex >= frameCount) {
        setIsResetting(true)
      } else {
        setFrameIndex(nextIndex)
      }
    }, duration)

    return () => clearTimeout(timerIdRef.current)
  }, [frameIndex, paused, isResetting, frameCount, getDurationMs])

  const goToNext = useCallback(() => {
    clearTimeout(timerIdRef.current)
    setIsResetting(false)
    setFrameIndex((prev) => (prev + 1) % frameCount)
  }, [frameCount])

  const goToPrev = useCallback(() => {
    clearTimeout(timerIdRef.current)
    setIsResetting(false)
    setFrameIndex((prev) => (prev - 1 + frameCount) % frameCount)
  }, [frameCount])

  return { frameIndex, goToNext, goToPrev }
}

interface UseImageCarouselReturn {
  readonly frameIndex: number
  readonly frame: CarouselFrame
  readonly isPaused: boolean
  readonly togglePause: () => void
  readonly goToNext: () => void
  readonly goToPrev: () => void
  readonly pause: () => void
}

function useImageCarousel(
  frames: readonly CarouselFrame[],
  reducedMotion: boolean,
): UseImageCarouselReturn {
  const [isPaused, setIsPaused] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- frames is a const 12-element array
  const getDurationMs = useCallback((i: number) => frames[i]!.durationMs, [frames])
  const {
    frameIndex,
    goToNext: timerGoToNext,
    goToPrev: timerGoToPrev,
  } = useFrameTimer(frames.length, getDurationMs, isPaused || reducedMotion)

  useEffect(() => {
    if (reducedMotion) return
    const nextIndex = (frameIndex + 1) % frames.length
    const img = new Image()
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- nextIndex is bounded by modulo
    img.src = frames[nextIndex]!.src
  }, [frameIndex, frames, reducedMotion])

  const togglePause = useCallback(() => setIsPaused((prev) => !prev), [])
  const pause = useCallback(() => setIsPaused(true), [])

  const goToNext = useCallback(() => {
    setIsPaused(true)
    timerGoToNext()
  }, [timerGoToNext])

  const goToPrev = useCallback(() => {
    setIsPaused(true)
    timerGoToPrev()
  }, [timerGoToPrev])

  if (reducedMotion) {
    return {
      frameIndex: frames.length - 1,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- frames is a const 12-element array
      frame: frames.at(-1)!,
      isPaused: false,
      togglePause,
      goToNext: NOOP,
      goToPrev: NOOP,
      pause: NOOP,
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- frameIndex is bounded
  const currentFrame = frames[frameIndex] ?? frames[0]!

  return { frameIndex, frame: currentFrame, isPaused, togglePause, goToNext, goToPrev, pause }
}

// -- MobileConversationCarousel component (private) --

const MobileConversationCarousel: FC<{ readonly reducedMotion: boolean }> = ({ reducedMotion }) => {
  const { frameIndex, frame, isPaused, togglePause, goToNext, goToPrev } = useImageCarousel(
    CAROUSEL_FRAMES,
    reducedMotion,
  )

  return (
    <Box
      component="section"
      role="region"
      aria-roledescription="carousel"
      aria-labelledby={SECTION_HEADING_ID}
      sx={sectionContainerSx}
    >
      <Typography component="h2" id={SECTION_HEADING_ID} sx={chapterTitleSx}>
        {HOME_CONTENT.conversation.title}
      </Typography>
      <Typography sx={chapterSubtitleSx}>{HOME_CONTENT.conversation.subtitle}</Typography>

      <Typography sx={visuallyHiddenSx}>
        {HOME_CONTENT.conversation.accessibilitySummary}
      </Typography>

      <Box sx={carouselContainerSx} aria-live={isPaused ? 'polite' : 'off'} aria-atomic="false">
        <Box
          role="group"
          aria-roledescription="slide"
          aria-label={`Slide ${frameIndex + 1} of ${CAROUSEL_FRAMES.length}`}
          aria-current="true"
          sx={slideWrapperSx}
        >
          <Box
            key={frameIndex}
            component="img"
            src={frame.src}
            alt={frame.alt}
            loading="eager"
            decoding="async"
            sx={carouselImageSx}
          />
        </Box>
      </Box>

      {!reducedMotion && (
        <Box sx={dotIndicatorRowSx} aria-hidden="true">
          {CAROUSEL_FRAMES.map((f, index) => (
            <Box key={f.src} sx={index === frameIndex ? activeDotSx : inactiveDotSx} />
          ))}
        </Box>
      )}

      {!reducedMotion && (
        <CarouselControls
          isPaused={isPaused}
          togglePause={togglePause}
          goToNext={goToNext}
          goToPrev={goToPrev}
        />
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Frame bubble renderer
// ---------------------------------------------------------------------------

const FrameBubble: FC<{ readonly frame: DemoConversationFrame }> = ({ frame }) => {
  const { bubbleType } = frame

  switch (bubbleType) {
    case 'user_prompt':
      return <UserInputBubble event={frame.event} />

    case 'agent_narrative':
      return <AgentNarrativeBubble event={frame.event} showTimestamp={true} />

    case 'notification':
      return (
        <NotificationBubble
          event={frame.event}
          session={DEMO_SESSION}
          latestEvent={frame.event}
          allUserCommands={frame.selectedOption === undefined ? [] : buildSelectedCommand(frame)}
          showUserOptions={true}
        >
          {renderNotificationContent(frame)}
        </NotificationBubble>
      )

    case 'file_modification':
      return <FileModificationBubble event={frame.event} showTimestamp={true} />

    case 'questions':
      return <DemoQuestionsBubble event={frame.event} selectedIndex={frame.selectedOption} />

    default:
      return bubbleType satisfies never
  }
}

function renderNotificationContent(frame: DemoConversationFrame) {
  const { event } = frame

  if (event.eventType === EventType.TOOL_PERMISSION_REQUEST) {
    const toolInput = event.notificationContext?.toolInput as BashToolInput | undefined
    if (toolInput) {
      return (
        <BashContextRenderer
          command={toolInput.command}
          description={toolInput.description}
          enableCollapse={false}
        />
      )
    }
  }

  if (event.eventType === EventType.PLAN_APPROVAL_REQUEST) {
    const toolInput = event.notificationContext?.toolInput as ExitPlanModeToolInput | undefined
    if (toolInput) {
      return <PlanApprovalContextRenderer plan={toolInput.plan} enableCollapse={false} />
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// Carousel controls (shared between desktop and mobile)
// ---------------------------------------------------------------------------

interface CarouselControlsProps {
  readonly isPaused: boolean
  readonly togglePause: () => void
  readonly goToNext: () => void
  readonly goToPrev: () => void
}

const CarouselControls: FC<CarouselControlsProps> = ({
  isPaused,
  togglePause,
  goToNext,
  goToPrev,
}) => (
  <Box sx={carouselControlBarSx}>
    <IconButton
      size="small"
      onClick={goToPrev}
      aria-label="Previous slide"
      sx={carouselControlButtonSx}
    >
      <ChevronLeftIcon />
    </IconButton>
    <IconButton
      size="small"
      onClick={togglePause}
      aria-label={isPaused ? 'Start automatic slide rotation' : 'Stop automatic slide rotation'}
      sx={carouselControlButtonSx}
    >
      {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
    </IconButton>
    <IconButton
      size="small"
      onClick={goToNext}
      aria-label="Next slide"
      sx={carouselControlButtonSx}
    >
      <ChevronRightIcon />
    </IconButton>
  </Box>
)

// ---------------------------------------------------------------------------
// User command builder for acknowledged frames
// ---------------------------------------------------------------------------

function buildSelectedCommand(frame: DemoConversationFrame): Command[] {
  if (frame.selectedOption === undefined) return []
  return [
    {
      commandType: CommandType.SUBMIT_OPTION,
      userCommandId: `demo-cmd-${frame.event.eventId}`,
      refEventId: frame.event.eventId,
      timestamp: new Date().toISOString(),
      userOption: { index: frame.selectedOption, option: '' },
      userPrompt: null,
      agentMode: null,
      userAnswers: null,
      errorRecovery: null,
    },
  ]
}

// ---------------------------------------------------------------------------
// Mock session (minimal shape for NotificationBubble)
// ---------------------------------------------------------------------------

const DEMO_SESSION_ID = 'demo-conversation-00000000'

const DEMO_CONVERSATION_TURN: ConversationTurn = {
  userPromptId: 'demo-turn-00000000',
  startTime: new Date().toISOString(),
  endTime: null,
  events: [],
  userCommands: [],
}

const DEMO_SESSION: ClaudeSession = {
  sessionId: DEMO_SESSION_ID,
  userExternalId: '00000000-0000-0000-0000-000000000000',
  transcriptPath: '/demo/transcript.json',
  contextWorkDirectory: '/home/dev/projects/nexus-web',
  tmuxSession: {
    sessionId: 'demo-tmux-00000000',
    sessionName: 'implement-oauth2',
    paneId: 'demo-pane-00000000',
  },
  userHostDetails: {
    hostId: 'demo-host-00000000',
    hostName: 'macbook-pro',
  },
  conversationTurns: new Map([['demo-turn-00000000', DEMO_CONVERSATION_TURN]]),
  sessionName: 'Implement OAuth2',
  sessionBreadcrumb: 'MacBook Pro > Nexus Web > Implement OAuth2',
  dangerouslySkipPermissions: false,
  autoModeSupported: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  runtime: createClaudeSessionRuntime(),
  isTransient: false,
}

// ---------------------------------------------------------------------------
// Playback hook
// ---------------------------------------------------------------------------

interface UseDemoConversationPlaybackReturn {
  readonly frameIndex: number
  readonly frame: DemoConversationFrame
  readonly isPaused: boolean
  readonly togglePause: () => void
  readonly goToNext: () => void
  readonly goToPrev: () => void
}

function useDemoConversationPlayback(): UseDemoConversationPlaybackReturn {
  const [isPaused, setIsPaused] = useState(false)

  const frames = CONVERSATION_DEMO_FRAMES
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- frames is a const 12-element array
  const getDurationMs = useCallback((i: number) => frames[i]!.durationMs, [frames])
  const {
    frameIndex,
    goToNext: timerGoToNext,
    goToPrev: timerGoToPrev,
  } = useFrameTimer(frames.length, getDurationMs, isPaused)

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- frameIndex is bounded, frames is never empty
  const currentFrame = frames[frameIndex] ?? frames[0]!

  const togglePause = useCallback(() => setIsPaused((prev) => !prev), [])

  const goToNext = useCallback(() => {
    setIsPaused(true)
    timerGoToNext()
  }, [timerGoToNext])

  const goToPrev = useCallback(() => {
    setIsPaused(true)
    timerGoToPrev()
  }, [timerGoToPrev])

  return {
    frameIndex,
    frame: currentFrame,
    isPaused,
    togglePause,
    goToNext,
    goToPrev,
  }
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sectionContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  py: { xs: 6, md: 10 },
  px: { xs: 2, md: 4 },
}

const chapterTitleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.75rem', md: '2.5rem' },
  fontWeight: 600,
  textAlign: 'center',
  mb: 1,
}

const chapterSubtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
}

const demoContainerSx: SxProps<Theme> = {
  maxWidth: 1000,
  width: '100%',
  mt: 6,
  position: 'relative',
}

const visuallyHiddenSx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
}

// -- Carousel styles --

const carouselContainerSx: SxProps<Theme> = (theme) => ({
  maxWidth: 1280,
  width: '100%',
  mt: { xs: 4, md: 6 },
  borderRadius: 2,
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(62, 39, 35, 0.10)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.3)'
      : '0 4px 24px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
  position: 'relative',
  cursor: 'default',
})

const carouselImageSx: SxProps<Theme> = {
  width: '100%',
  height: 'auto',
  display: 'block',
  '@keyframes carouselFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  animation: 'carouselFadeIn 400ms ease-in-out',
}

const slideWrapperSx: SxProps<Theme> = {
  display: 'block',
  width: '100%',
}

const carouselControlBarSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 1,
  mt: 1,
}

const carouselControlButtonSx: SxProps<Theme> = {
  color: 'text.secondary',
  width: 44,
  height: 44,
  padding: 0,
  '&:hover': {
    color: 'text.primary',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
}

const dotIndicatorRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  gap: 0.75,
  mt: 1.5,
  opacity: 0.6,
}

const activeDotSx: SxProps<Theme> = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  bgcolor: 'text.secondary',
  transition: 'all 200ms ease-in-out',
}

const inactiveDotSx: SxProps<Theme> = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  bgcolor: 'text.disabled',
  transition: 'all 200ms ease-in-out',
}
